import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import {
  AlertTriangle,
  Check,
  FileAudio,
  Lightbulb,
  ListTodo,
  LoaderCircle,
  Mic,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Square,
  Upload
} from "lucide-react";
import clsx from "clsx";
import type {
  Task,
  Thought,
  ThoughtCategory,
  VoiceOrganization
} from "../types";
import { ApiError, organizeVoiceTranscript, transcribeAudio } from "../lib/api";
import { makeId, saveRecord } from "../lib/db";
import { THOUGHT_CATEGORIES } from "../lib/categories";
import { nowIso } from "../lib/time";
import { Button, Field, Modal } from "./ui";

type InputMode = "record" | "upload" | "text";

function preferredAudioType(): string {
  const candidates = [
    "audio/mp4",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus"
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
}

function audioExtension(type: string): string {
  if (type.includes("mp4")) return "m4a";
  if (type.includes("ogg")) return "ogg";
  return "webm";
}

export function VoiceOrganizer({
  onClose,
  notify
}: {
  onClose: () => void;
  notify: (message: string, tone?: "default" | "error") => void;
}) {
  const [mode, setMode] = useState<InputMode>("record");
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioFile, setAudioFile] = useState<File>();
  const [audioUrl, setAudioUrl] = useState("");
  const [transcript, setTranscript] = useState("");
  const [transcribing, setTranscribing] = useState(false);
  const [organizing, setOrganizing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<VoiceOrganization>();
  const [thoughtCategories, setThoughtCategories] = useState<ThoughtCategory[]>([]);
  const [selectedThoughts, setSelectedThoughts] = useState<number[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
  const [rawCategory, setRawCategory] = useState<ThoughtCategory>("other");

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const audioUrlRef = useRef("");
  const resultRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach((track) => track.stop());
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (result) {
      window.setTimeout(
        () => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        80
      );
    }
  }, [result]);

  function setAudio(file: File) {
    if (file.size > 20 * 1024 * 1024) {
      setError("录音文件必须小于 20MB。");
      return;
    }
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    const nextUrl = URL.createObjectURL(file);
    audioUrlRef.current = nextUrl;
    setAudioUrl(nextUrl);
    setAudioFile(file);
    setTranscript("");
    setResult(undefined);
    setError("");
  }

  async function startRecording() {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("当前浏览器不支持直接录音，请改用上传音频或粘贴文字。");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mimeType = preferredAudioType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size > 0) {
          setAudio(
            new File([blob], `voice-${format(new Date(), "yyyyMMdd-HHmm")}.${audioExtension(type)}`, {
              type
            })
          );
        }
        stream.getTracks().forEach((track) => track.stop());
      };
      recorder.start(1000);
      setElapsed(0);
      setRecording(true);
      timerRef.current = window.setInterval(() => {
        setElapsed((current) => {
          if (current >= 599) {
            if (timerRef.current !== null) window.clearInterval(timerRef.current);
            timerRef.current = null;
            setRecording(false);
            if (recorder.state === "recording") recorder.stop();
            return 600;
          }
          return current + 1;
        });
      }, 1000);
    } catch {
      setError("没有取得麦克风权限。请在 Safari 设置中允许麦克风，或上传已有录音。");
    }
  }

  function stopRecording() {
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    timerRef.current = null;
    setRecording(false);
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }

  async function transcribe() {
    if (!audioFile) {
      setError("请先录音或选择一个音频文件。");
      return;
    }
    setTranscribing(true);
    setError("");
    try {
      const response = await transcribeAudio(audioFile, audioFile.name, elapsed || undefined);
      setTranscript(response.text);
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.message
          : "录音转写失败，请稍后重试。"
      );
    } finally {
      setTranscribing(false);
    }
  }

  async function organize() {
    if (transcript.trim().length < 10) {
      setError("至少需要约十个字的有效内容才能整理。");
      return;
    }
    setOrganizing(true);
    setError("");
    try {
      const proposal = await organizeVoiceTranscript(
        transcript.trim(),
        mode === "record" ? "recording" : mode
      );
      setResult(proposal);
      setThoughtCategories(proposal.thoughts.map((thought) => thought.category));
      setSelectedThoughts(proposal.thoughts.map((_, index) => index));
      setSelectedTasks(proposal.tasks.map((_, index) => index));
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.message
          : "AI 没有整理成功，但转写文字仍然保留。"
      );
    } finally {
      setOrganizing(false);
    }
  }

  async function saveRawTranscript() {
    if (!transcript.trim()) return;
    setSaving(true);
    const timestamp = nowIso();
    try {
      await saveRecord<Thought>({
        id: makeId(),
        kind: "thought",
        title: `${mode === "text" ? "文字" : "语音"}思考 · ${format(new Date(), "M月d日 HH:mm")}`,
        body: transcript.trim(),
        createdAt: timestamp,
        updatedAt: timestamp,
        revision: 0,
        status: "open",
        category: rawCategory,
        facts: "",
        judgment: "",
        evidence: "",
        nextStep: "",
        keyPoints: [],
        transcript: transcript.trim(),
        sourceType: mode === "text" ? "text" : "voice"
      });
      notify("原始内容已保存为思考");
      onClose();
    } finally {
      setSaving(false);
    }
  }

  async function applyResult() {
    if (!result) return;
    setSaving(true);
    const timestamp = nowIso();
    const thoughtIds: string[] = [];
    try {
      for (const index of selectedThoughts) {
        const proposal = result.thoughts[index];
        if (!proposal) continue;
        const id = makeId();
        thoughtIds.push(id);
        await saveRecord<Thought>({
          id,
          kind: "thought",
          title: proposal.title,
          body: proposal.summary,
          createdAt: timestamp,
          updatedAt: timestamp,
          revision: 0,
          status: "open",
          category: thoughtCategories[index] ?? proposal.category,
          facts: proposal.facts,
          judgment: proposal.judgment,
          evidence: proposal.evidence,
          nextStep: proposal.nextStep,
          reviewAt: proposal.reviewAt,
          keyPoints: proposal.keyPoints,
          transcript: transcript.trim(),
          sourceType: mode === "text" ? "text" : "voice"
        });
      }
      for (const index of selectedTasks) {
        const proposal = result.tasks[index];
        if (!proposal) continue;
        const complete = Boolean(proposal.outcome?.trim() && proposal.nextAction?.trim());
        await saveRecord<Task>({
          id: makeId(),
          kind: "task",
          title: proposal.title,
          body: "",
          createdAt: timestamp,
          updatedAt: timestamp,
          revision: 0,
          sourceIds: thoughtIds,
          status: complete ? "next" : "inbox",
          outcome: proposal.outcome ?? "",
          nextAction: proposal.nextAction ?? "",
          area: proposal.area ?? "",
          dueAt: proposal.dueAt,
          sourceType: mode === "text" ? "text" : "voice",
          transcript: transcript.trim()
        });
      }
      const thoughtCount = selectedThoughts.length;
      const taskCount = selectedTasks.length;
      notify(`已加入 ${thoughtCount} 条思考、${taskCount} 项任务`);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  function reset() {
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    audioUrlRef.current = "";
    setAudioFile(undefined);
    setAudioUrl("");
    setTranscript("");
    setResult(undefined);
    setError("");
    setElapsed(0);
    setSelectedThoughts([]);
    setSelectedTasks([]);
  }

  const footer = result ? (
    <>
      <span className="ai-footer-note">
        已选 {selectedThoughts.length} 条思考、{selectedTasks.length} 项任务
      </span>
      <Button type="button" onClick={onClose}>取消</Button>
      <Button
        type="button"
        variant="primary"
        disabled={saving || (selectedThoughts.length === 0 && selectedTasks.length === 0)}
        onClick={() => void applyResult()}
      >
        {saving ? "正在写入…" : "确认加入工作台"}
      </Button>
    </>
  ) : (
    <>
      <Button type="button" onClick={onClose}>取消</Button>
      {transcript.trim() && (
        <Button type="button" onClick={() => void saveRawTranscript()} disabled={saving}>
          {saving ? "保存中…" : "仅保存原文"}
        </Button>
      )}
      <Button
        type="button"
        variant="primary"
        onClick={() => void organize()}
        disabled={organizing || transcript.trim().length < 10}
      >
        {organizing ? <LoaderCircle size={16} className="spin" /> : <Sparkles size={16} />}
        {organizing ? "正在提炼…" : "AI 提炼与分类"}
      </Button>
    </>
  );

  return (
    <Modal
      title="语音与长文本整理"
      eyebrow="语音收件箱"
      onClose={onClose}
      size="large"
      footer={footer}
    >
      <div className="voice-organizer">
        <div className="voice-privacy">
          <ShieldCheck size={19} />
          <p>
            录音先留在本机。只有点击“转写录音”后才上传到 Cloudflare 转写；
            点击“AI 提炼”后，转写文字才会进入整理模型。结果先预览，确认后才写入。
          </p>
        </div>

        <div className="voice-source-tabs" role="group" aria-label="语音来源">
          <button type="button" disabled={recording} className={mode === "record" ? "active" : ""} onClick={() => { setMode("record"); reset(); }}>
            <Mic size={18} /> 直接录音
          </button>
          <button type="button" disabled={recording} className={mode === "upload" ? "active" : ""} onClick={() => { setMode("upload"); reset(); }}>
            <Upload size={18} /> 上传音频
          </button>
          <button type="button" disabled={recording} className={mode === "text" ? "active" : ""} onClick={() => { setMode("text"); reset(); }}>
            <FileAudio size={18} /> 粘贴口语文字
          </button>
        </div>

        {mode === "record" && (
          <section className="record-panel">
            {!audioFile ? (
              <>
                <button
                  type="button"
                  className={clsx("record-button", recording && "recording")}
                  onClick={() => (recording ? stopRecording() : void startRecording())}
                  aria-label={recording ? "停止录音" : "开始录音"}
                >
                  {recording ? <Square size={25} /> : <Mic size={30} />}
                </button>
                <h3>{recording ? `正在录音 ${formatDuration(elapsed)}` : "点击开始录音"}</h3>
                <p>{recording ? "说完后再点一次停止。" : "最长 10 分钟，结束后可先回放再转写。"}</p>
              </>
            ) : (
              <div className="audio-ready">
                <FileAudio size={24} />
                <div>
                  <strong>{audioFile.name}</strong>
                  <small>{formatFileSize(audioFile.size)}</small>
                </div>
                <button type="button" onClick={reset} aria-label="重新录音"><RotateCcw size={17} /></button>
              </div>
            )}
          </section>
        )}

        {mode === "upload" && (
          <label className="audio-upload">
            <Upload size={24} />
            <strong>{audioFile ? audioFile.name : "选择一个录音文件"}</strong>
            <small>支持 M4A、MP3、WAV、WebM、MP4，最大 20MB</small>
            <input
              type="file"
              accept="audio/*,video/mp4"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) setAudio(file);
              }}
            />
          </label>
        )}

        {audioUrl && mode !== "text" && (
          <audio className="audio-player" controls src={audioUrl}>
            <track kind="captions" />
          </audio>
        )}

        {mode !== "text" && audioFile && !transcript && (
          <Button type="button" className="transcribe-button" onClick={() => void transcribe()} disabled={transcribing}>
            {transcribing ? <LoaderCircle size={17} className="spin" /> : <Sparkles size={17} />}
            {transcribing ? "正在转写，可能需要一点时间…" : "转写这段录音"}
          </Button>
        )}

        {(mode === "text" || transcript) && (
          <Field
            label={mode === "text" ? "粘贴或直接输入口语化文字" : "转写文字（可直接修改）"}
            hint="错字、专有名词和上下文都可以先修一下，再让 AI 整理。"
          >
            <textarea
              className="textarea voice-transcript"
              rows={9}
              value={transcript}
              onChange={(event) => {
                setTranscript(event.target.value);
                setResult(undefined);
              }}
              placeholder={
                mode === "text"
                  ? "把输入法转录的一整段口语直接粘贴进来，不需要自己先整理……"
                  : "转写结果会出现在这里"
              }
            />
          </Field>
        )}

        {error && <p className="form-error">{error}</p>}

        {!result && transcript.trim() && (
          <div className="raw-save-row">
            <span>AI 不可用或只想原样保留？</span>
            <select
              className="input compact-select"
              value={rawCategory}
              onChange={(event) => setRawCategory(event.target.value as ThoughtCategory)}
            >
              {THOUGHT_CATEGORIES.map((category) => (
                <option key={category.value} value={category.value}>{category.label}</option>
              ))}
            </select>
          </div>
        )}

        {result && (
          <section className="voice-result" ref={resultRef}>
            <header>
              <p className="eyebrow">整理结果</p>
              <h3>{result.headline}</h3>
              <p>{result.summary}</p>
            </header>

            {result.warnings.length > 0 && (
              <div className="voice-warnings">
                <AlertTriangle size={18} />
                <div>
                  {result.warnings.map((warning) => <p key={warning}>{warning}</p>)}
                </div>
              </div>
            )}

            <div className="voice-result-section">
              <div className="voice-result-heading">
                <Lightbulb size={18} />
                <h3>思考建议</h3>
                <span>{selectedThoughts.length}/{result.thoughts.length}</span>
              </div>
              {result.thoughts.length === 0 ? (
                <p className="muted">这次没有需要单独沉淀的新思考。</p>
              ) : (
                <div className="voice-proposal-list">
                  {result.thoughts.map((thought, index) => {
                    const selected = selectedThoughts.includes(index);
                    return (
                      <article key={`${thought.title}-${index}`} className={clsx("voice-proposal", selected && "selected")}>
                        <label className="proposal-check">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() =>
                              setSelectedThoughts((current) =>
                                selected ? current.filter((item) => item !== index) : [...current, index]
                              )
                            }
                          />
                          <span><Check size={14} /></span>
                        </label>
                        <div className="voice-proposal-content">
                          <div className="voice-proposal-title">
                            <h4>{thought.title}</h4>
                            <select
                              className="compact-select"
                              value={thoughtCategories[index] ?? thought.category}
                              onChange={(event) =>
                                setThoughtCategories((current) => {
                                  const next = [...current];
                                  next[index] = event.target.value as ThoughtCategory;
                                  return next;
                                })
                              }
                            >
                              {THOUGHT_CATEGORIES.map((category) => (
                                <option key={category.value} value={category.value}>{category.label}</option>
                              ))}
                            </select>
                          </div>
                          <p>{thought.summary}</p>
                          {thought.keyPoints.length > 0 && (
                            <ul>
                              {thought.keyPoints.map((point) => <li key={point}>{point}</li>)}
                            </ul>
                          )}
                          {thought.nextStep && <small>下一步：{thought.nextStep}</small>}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="voice-result-section">
              <div className="voice-result-heading">
                <ListTodo size={18} />
                <h3>任务建议</h3>
                <span>{selectedTasks.length}/{result.tasks.length}</span>
              </div>
              {result.tasks.length === 0 ? (
                <p className="muted">这次只有思考，没有需要立即执行的任务。</p>
              ) : (
                <div className="voice-proposal-list">
                  {result.tasks.map((task, index) => {
                    const selected = selectedTasks.includes(index);
                    return (
                      <article key={`${task.title}-${index}`} className={clsx("voice-proposal task", selected && "selected")}>
                        <label className="proposal-check">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() =>
                              setSelectedTasks((current) =>
                                selected ? current.filter((item) => item !== index) : [...current, index]
                              )
                            }
                          />
                          <span><Check size={14} /></span>
                        </label>
                        <div className="voice-proposal-content">
                          <h4>{task.title}</h4>
                          <p><strong>预期结果：</strong>{task.outcome || "待补充"}</p>
                          <p><strong>下一步：</strong>{task.nextAction || "待补充"}</p>
                          <small>{task.area || "未分类"}{task.dueAt ? ` · ${task.dueAt}` : ""}</small>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
}

function formatDuration(seconds: number): string {
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
